import React from 'react';
import { Plus, Printer, Users, X, CheckCircle, ChefHat, Wine, Send, Banknote, CreditCard } from 'lucide-react';
import { formatPrice } from '../../utils/translations';
import { BEVERAGE_CATEGORIES } from './posConstants';

export const OrderDetailView = ({
  selectedTable, setSelectedTable, setViewMode, setTableInput,
  billDiscountPercent, setBillDiscountPercent, billPaymentMethod, setBillPaymentMethod,
  settings, printTableBill, openSplitBill, closeTable,
  transferOrder, updateOrderStatus, updateKitchenStatus, updateBeverageStatus,
  cancelOrder, t
}) => {
  // Status info helper
  const getStatusInfo = (order) => {
    if (order.status === 'cancelled') return { label: t.cancel, color: 'bg-red-500/20 text-red-400', icon: 'X' };
    if (order.status === 'served') return { label: t.served, color: 'bg-emerald-500/20 text-emerald-400', icon: 'ok' };
    if (order.status === 'ready') return { label: t.ready, color: 'bg-green-500/20 text-green-400', icon: 'bell' };
    if (order.status === 'preparing') return { label: t.preparing, color: 'bg-blue-500/20 text-blue-400', icon: 'fire' };
    if (order.status === 'pending') return { label: t.transferred, color: 'bg-purple-500/20 text-purple-400', icon: 'send' };
    if (order.status === 'pending_approval') return { label: t.pendingApproval, color: 'bg-amber-500/20 text-amber-400', icon: 'wait' };
    return { label: t.pending, color: 'bg-slate-500/20 text-slate-400', icon: 'wait' };
  };

  // Calculate bill summary
  const sessionSubtotal = (selectedTable.orders || []).reduce((sum, o) =>
    sum + (o.items || []).reduce((s, i) => s + (i.price * i.quantity), 0), 0
  );
  const discountAmt = sessionSubtotal * (billDiscountPercent / 100);
  const afterDiscount = sessionSubtotal - discountAmt;
  const bankFee = billPaymentMethod === 'card' && settings?.enable_credit_card_fee
    ? afterDiscount * (settings?.credit_card_fee_percent || 3) / 100
    : 0;
  const finalTotal = afterDiscount + bankFee;

  return (
    <div className="flex-1 overflow-y-auto p-6" data-testid="order-detail-view">
      <div className="max-w-4xl mx-auto">
        {/* Table Header */}
        <div className="bg-slate-800 rounded-2xl p-6 mb-6 border border-slate-700">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-3xl font-black">{t.table} {selectedTable.table}</h2>
              <p className="text-slate-400">{t.openSince} {new Date(selectedTable.created_at).toLocaleTimeString()}</p>
            </div>
            <button
              onClick={() => { setSelectedTable(null); setViewMode('dashboard'); }}
              className="p-2 bg-slate-700 rounded-xl hover:bg-slate-600"
              data-testid="close-order-detail"
            >
              <X size={20} />
            </button>
          </div>

          {/* Bill Summary with Discount & Bank Fee */}
          <div className="space-y-4 mb-6">
            {/* Discount Selection */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">{t.discount}</label>
              <div className="flex gap-2">
                {[0, 5, 10, 15, 20].map(d => (
                  <button
                    key={d}
                    onClick={() => setBillDiscountPercent(d)}
                    className={`flex-1 py-2 rounded-xl font-bold text-sm transition-all ${
                      billDiscountPercent === d
                        ? 'bg-amber-500 text-slate-900'
                        : 'bg-slate-700 hover:bg-slate-600'
                    }`}
                    data-testid={`bill-discount-${d}`}
                  >
                    {d}%
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">{t.paymentMethod}</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setBillPaymentMethod('cash')}
                  className={`flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${
                    billPaymentMethod === 'cash'
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-700 hover:bg-slate-600'
                  }`}
                  data-testid="bill-pay-cash"
                >
                  <Banknote size={18} />
                  {t.cash}
                </button>
                <button
                  onClick={() => setBillPaymentMethod('card')}
                  className={`flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${
                    billPaymentMethod === 'card'
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-700 hover:bg-slate-600'
                  }`}
                  data-testid="bill-pay-card"
                >
                  <CreditCard size={18} />
                  {t.card}
                </button>
              </div>
            </div>

            {/* Bill Summary */}
            <div className="bg-slate-700/50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">{t.subtotal}</span>
                <span>{formatPrice(sessionSubtotal)}</span>
              </div>
              {billDiscountPercent > 0 && (
                <div className="flex justify-between text-sm text-emerald-400">
                  <span>{t.discount} ({billDiscountPercent}%)</span>
                  <span>-{formatPrice(discountAmt)}</span>
                </div>
              )}
              {bankFee > 0 && (
                <div className="flex justify-between text-sm text-blue-400">
                  <span>{t.cardFee} ({settings?.credit_card_fee_percent || 3}%)</span>
                  <span>+{formatPrice(bankFee)}</span>
                </div>
              )}
              <div className="border-t border-slate-600 pt-2 mt-2">
                <div className="flex justify-between text-lg">
                  <span className="font-bold">{t.total}</span>
                  <span className="font-black text-amber-400">{formatPrice(finalTotal)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => { setTableInput(selectedTable.table); setViewMode('menu'); }}
              className="flex-1 py-3 bg-slate-700 rounded-xl font-bold hover:bg-slate-600 flex items-center justify-center gap-2"
              data-testid="add-items-btn"
            >
              <Plus size={18} />
              {t.addItems}
            </button>
            <button
              onClick={() => printTableBill(selectedTable)}
              className="flex-1 py-3 bg-slate-700 rounded-xl font-bold hover:bg-slate-600 flex items-center justify-center gap-2"
              data-testid="print-bill-btn"
            >
              <Printer size={18} />
              {t.printBill}
            </button>
            <button
              onClick={() => openSplitBill(selectedTable)}
              className="flex-1 py-3 bg-blue-600 rounded-xl font-black hover:bg-blue-500 flex items-center justify-center gap-2"
              data-testid="split-bill-btn"
            >
              <Users size={18} />
              {t.splitBill}
            </button>
            <button
              onClick={() => closeTable(selectedTable)}
              className="flex-1 py-3 bg-amber-500 text-slate-900 rounded-xl font-black hover:bg-amber-400 flex items-center justify-center gap-2"
              data-testid="close-bill-btn"
            >
              <CheckCircle size={18} />
              {t.closeBill}
            </button>
          </div>
        </div>

        {/* Order History with Status Management */}
        <h3 className="text-lg font-black mb-4">{t.orderHistory}</h3>
        <div className="space-y-4">
          {(selectedTable.orders || []).map((order, idx) => {
            const foodItems = (order.items || []).filter(item => !BEVERAGE_CATEGORIES.includes(item.category));
            const drinkItems = (order.items || []).filter(item => BEVERAGE_CATEGORIES.includes(item.category));
            const orderTotal = (order.items || []).reduce((s, i) => s + (i.price * i.quantity), 0);
            const statusInfo = getStatusInfo(order);

            return (
              <div key={order.id || idx} className={`bg-slate-800 rounded-2xl p-5 border ${order.status === 'cancelled' ? 'border-red-500/50 opacity-60' : 'border-slate-700'}`} data-testid={`order-history-${order.id || idx}`}>
                {/* Order Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-black">#{order.order_number || idx + 1}</span>
                    <span className={`px-3 py-1 rounded-lg text-xs font-bold ${statusInfo.color}`}>
                      {statusInfo.icon} {statusInfo.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-amber-400">{formatPrice(orderTotal)}</span>
                    <span className="text-sm text-slate-500">
                      {new Date(order.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                </div>

                {/* Kitchen & Bar Status */}
                {order.status !== 'cancelled' && order.status !== 'served' && (
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {foodItems.length > 0 && (
                      <div className="bg-amber-500/10 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-bold text-amber-400 flex items-center gap-1">
                            <ChefHat size={14} /> {t.food} ({foodItems.length})
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            order.kitchen_status === 'ready' ? 'bg-green-500 text-white' :
                            order.kitchen_status === 'preparing' ? 'bg-blue-500 text-white' :
                            'bg-amber-500 text-slate-900'
                          }`}>
                            {order.kitchen_status === 'ready' ? 'Ready' :
                             order.kitchen_status === 'preparing' ? 'Cooking' : 'Waiting'}
                          </span>
                        </div>
                        {order.kitchen_status !== 'ready' && order.kitchen_status !== 'served' && (
                          <div className="flex gap-1">
                            {order.kitchen_status === 'pending' && (
                              <button onClick={() => updateKitchenStatus(order.id, 'preparing')} className="flex-1 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-bold">Start</button>
                            )}
                            {order.kitchen_status === 'preparing' && (
                              <button onClick={() => updateKitchenStatus(order.id, 'ready')} className="flex-1 py-1.5 bg-green-500 text-white rounded-lg text-xs font-bold">Ready</button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {drinkItems.length > 0 && (
                      <div className="bg-purple-500/10 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-bold text-purple-400 flex items-center gap-1">
                            <Wine size={14} /> {t.drinks} ({drinkItems.length})
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            order.beverage_status === 'ready' ? 'bg-green-500 text-white' :
                            order.beverage_status === 'preparing' ? 'bg-blue-500 text-white' :
                            'bg-purple-500 text-white'
                          }`}>
                            {order.beverage_status === 'ready' ? 'Ready' :
                             order.beverage_status === 'preparing' ? 'Making' : 'Waiting'}
                          </span>
                        </div>
                        {order.beverage_status !== 'ready' && order.beverage_status !== 'served' && (
                          <div className="flex gap-1">
                            {order.beverage_status === 'pending' && (
                              <button onClick={() => updateBeverageStatus(order.id, 'preparing')} className="flex-1 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-bold">Start</button>
                            )}
                            {order.beverage_status === 'preparing' && (
                              <button onClick={() => updateBeverageStatus(order.id, 'ready')} className="flex-1 py-1.5 bg-green-500 text-white rounded-lg text-xs font-bold">Ready</button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Items List */}
                <div className="space-y-2 mb-4">
                  {(order.items || []).map((item, itemIdx) => (
                    <div key={itemIdx} className="flex justify-between text-sm">
                      <span className="text-slate-300 flex items-center gap-2">
                        <span className={BEVERAGE_CATEGORIES.includes(item.category) ? 'text-purple-400' : 'text-amber-400'}>
                          
                        </span>
                        {item.quantity}x {item.name?.en || item.name?.vi || item.name}
                      </span>
                      <span className="font-bold">{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>

                {/* Order Actions */}
                {order.status !== 'cancelled' && order.status !== 'served' && (
                  <div className="flex gap-2 pt-3 border-t border-slate-700">
                    {order.status === 'pending_approval' && (
                      <button
                        onClick={() => transferOrder(order)}
                        className="flex-1 py-2 bg-emerald-500 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-1"
                      >
                        <Send size={14} /> {t.sendAll}
                      </button>
                    )}
                    {order.status === 'ready' && (
                      <button
                        onClick={() => updateOrderStatus(order.id, 'served')}
                        className="flex-1 py-2 bg-emerald-500 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-1"
                      >
                        <CheckCircle size={14} /> {t.markServed}
                      </button>
                    )}
                    <button
                      onClick={() => cancelOrder(order.id)}
                      className="py-2 px-3 bg-red-500/20 text-red-400 rounded-lg text-sm font-bold hover:bg-red-500/30"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
